import {
  PORTAL_FLYWHEEL_ROUTING_URL,
  isLaborPortalHostname,
  portalAuthzHeaders,
} from '../../constants';
import type { ReasoningTrace } from './reasoningTrace';

export interface PortalFlywheelRoutingPayload {
  request_id: string;
  execution_id: string;
  intent: string;
  path: string;
  user_comment?: string;
  ts: string;
}

interface PortalFlywheelRoutingResponse {
  ok?: boolean;
  message?: string;
  error?: string;
}

export function isPortalFlywheelRoutingEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    import.meta.env.VITE_PORTAL_FLYWHEEL_ROUTING_ENABLED === 'true' &&
    isLaborPortalHostname(window.location.hostname)
  );
}

export function buildPortalFlywheelRoutingPayload(
  trace: ReasoningTrace,
  userComment?: string,
): PortalFlywheelRoutingPayload {
  const comment = userComment?.trim().slice(0, 1000);
  return {
    request_id: String(trace.request_id ?? '').trim().slice(0, 200),
    execution_id: String(trace.execution_id ?? '').trim().slice(0, 200),
    intent: String(trace.intent ?? '').trim().slice(0, 200),
    path: String(trace.path ?? '').trim().slice(0, 400),
    ...(comment ? { user_comment: comment } : {}),
    ts: new Date().toISOString(),
  };
}

export async function reportWrongRoutingPath(
  trace: ReasoningTrace,
  userComment?: string,
): Promise<string> {
  if (!isPortalFlywheelRoutingEnabled()) {
    throw new Error('Diese Feedback-Funktion ist nur im Labor verfügbar.');
  }

  const payload = buildPortalFlywheelRoutingPayload(trace, userComment);
  if (!payload.request_id && !payload.execution_id) {
    throw new Error('Die Routing-Referenz fehlt. Bitte ARKORBIT informieren.');
  }

  const response = await fetch(PORTAL_FLYWHEEL_ROUTING_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...portalAuthzHeaders(PORTAL_FLYWHEEL_ROUTING_URL),
    },
    body: JSON.stringify(payload),
  });
  const data = (await response.json().catch(() => ({}))) as PortalFlywheelRoutingResponse;
  if (!response.ok || data.ok === false) {
    throw new Error(data.error || `Feedback konnte nicht gespeichert werden (HTTP ${response.status}).`);
  }
  return data.message || 'Danke. Der falsche Routing-Pfad wurde für die Labor-Auswertung gespeichert.';
}
